import { InboxOutlined } from '@ant-design/icons'
import {
  Form,
  FormItemProps,
  Space,
  Typography,
  Upload,
  UploadProps,
} from 'antd'
import { UploadFile } from 'antd/es/upload'
import ImgCrop from 'antd-img-crop'
import { ReactNode, useCallback, useMemo, useRef } from 'react'
import {
  AttachmentType,
  attachmentTypes as attachmentTypesBase,
  ExpectedImage,
  FileType,
  formatBytes,
  formatExpectedImage,
  resolveAspect,
} from '../utils/AttachmentFile'
import { doesntExceedSize } from '../utils/rules'

const { Dragger } = Upload
const { Text } = Typography

interface Props {
  label?: ReactNode
  hideCloseIcon?: boolean
  hideLabel?: boolean
  formItem?: {
    name: FormItemProps['name']
    rules: FormItemProps['rules']
  }
  dragger?: {
    multiple?: boolean
    fileTypes?: FileType[]
    title?: string
    subtitle?: string
  }
  /**
   * Optional description of the expected image. When set (and the uploader
   * accepts pictures), it renders an auto help line and enables a crop modal
   * (locked ratio, zoom + rotation) with output downscaled to the target size.
   * Omitting it keeps the legacy behavior untouched. Crop never runs for PDFs.
   */
  image?: ExpectedImage
  noStyle?: boolean
}

/** Downscale an image File to fit within a target box, preserving its ratio. */
const downscaleImageFile = (
  file: File,
  image: ExpectedImage
): Promise<File> => {
  const maxWidth = image.maxWidth ?? image.width
  const maxHeight = image.maxHeight ?? image.height
  if (!maxWidth && !maxHeight) return Promise.resolve(file)

  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      const { width, height } = img
      const scale = Math.min(
        maxWidth ? maxWidth / width : 1,
        maxHeight ? maxHeight / height : 1,
        1 // never upscale
      )
      if (scale >= 1) {
        URL.revokeObjectURL(url)
        resolve(file)
        return
      }
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(width * scale)
      canvas.height = Math.round(height * scale)
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        URL.revokeObjectURL(url)
        resolve(file)
        return
      }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url)
          if (!blob) {
            resolve(file)
            return
          }
          resolve(new File([blob], file.name, { type: file.type }))
        },
        file.type,
        0.9
      )
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(file)
    }
    img.src = url
  })
}

/**
 * What antd-img-crop paints across the crop canvas before drawing the image.
 * Its default is opaque white, which flattens every transparent PNG that goes
 * through the crop modal.
 *
 * Written as `rgba(0, 0, 0, 0)` rather than `transparent` on purpose: an
 * unparseable `fillStyle` is silently ignored by the canvas, leaving the
 * default *opaque black* — so the literal that cannot be misparsed is the safe
 * one. Filling a fresh canvas with transparent black is a no-op under
 * `source-over`, and the canvas is created per crop, so nothing leaks between
 * uploads.
 *
 * This cannot be decided per file: the library captures `fillColor` in the
 * closure it builds *before* calling `beforeCrop`, so any state set from there
 * only lands on the following crop. Constant value, correct every time.
 *
 * Consequence for JPEG, which has no alpha: the area a rotation leaves
 * uncovered now encodes as black instead of white. Only reachable by using the
 * rotation slider; flattening non-alpha output onto white after the crop would
 * be the fix, at the cost of an extra re-encode on every JPEG.
 */
const CROP_FILL_COLOR = 'rgba(0, 0, 0, 0)'

const AttachmentEditor = ({
  label = 'Attachment',
  hideLabel = false,
  formItem = {
    name: 'file',
    rules: [],
  },
  dragger = {
    multiple: false,
    fileTypes: undefined,
  },
  image,
  noStyle,
}: Props) => {
  // An uploader that names no file type accepts them all. Resolved once: read
  // twice with different fallbacks, it used to accept a picture for the help
  // line and the downscale while refusing it for the crop modal.
  const fileTypes = useMemo(
    () => dragger.fileTypes ?? (Object.keys(attachmentTypesBase) as FileType[]),
    [dragger.fileTypes]
  )

  const attachmentTypes = useCallback(
    (): AttachmentType[] => fileTypes.map((t) => attachmentTypesBase[t]),
    [fileTypes]
  )

  const form = Form.useFormInstance()
  const name = formItem.name
  const watched = Form.useWatch(name, form) as UploadFile[] | undefined
  const valueList = Array.isArray(watched) ? watched : []

  const acceptsPicture = fileTypes.includes('picture')
  const cropEnabled = !!image && acceptsPicture
  const helpLine = image ? formatExpectedImage(image) : undefined

  const validateFiles = (fileList: UploadFile[]) => {
    return fileList.map((file) => {
      const attachmentType = attachmentTypes().find((at) =>
        at.accept.includes(file.type as FileType)
      )

      if (
        !attachmentType ||
        (file.size && file.size > attachmentType.maxSize)
      ) {
        return { ...file, status: 'error' }
      }
      return file
    })
  }

  /** Single writer for the field value. */
  const writeValue = (list: UploadFile[]) => {
    form.setFieldValue(name, validateFiles(list))

    // On the crop path the form item wraps `ImgCrop`, which rebuilds the inner
    // `Upload` from its child's props alone and drops the `onChange` the item
    // injected. Nothing carries the change back, so `validateTrigger` never
    // fires and a "required" error would sit there after a file was added.
    if (cropEnabled) {
      form.validateFields([name]).catch(() => {})
    }
  }

  // Non-crop path (PDF, or a picture uploader without an expected image):
  // legacy behavior, onChange writes the value and downscales any fresh image.
  const handleChange = async (fileList: UploadFile[]) => {
    let list = fileList
    if (image) {
      list = await Promise.all(
        fileList.map(async (file) => {
          const origin = file.originFileObj as File | undefined
          if (
            origin &&
            origin.type?.startsWith('image/') &&
            !(file as UploadFile & { __downscaled?: boolean }).__downscaled
          ) {
            const resized = await downscaleImageFile(origin, image)
            return Object.assign({}, file, {
              originFileObj: Object.assign(resized, { uid: file.uid }),
              size: resized.size,
              __downscaled: true,
            }) as UploadFile
          }
          return file
        })
      )
    }
    writeValue(list)
  }

  // Crop path. Under antd 6 the fileList's originFileObj always holds the
  // pre-crop original (antd rebuilds the list from the raw `origin`, never from
  // the transformed file). The cropped File is only reachable via ImgCrop's
  // onModalOk, where it carries the SAME uid as the matching fileList item.
  // We stash it by uid, then rebuild the value in onChange by swapping each
  // item's originFileObj for its cropped + downscaled version. Letting antd own
  // the fileList keeps multi-select and removals correct (no double, no loss).
  const croppedByUid = useRef(new Map<string, File>())
  const readyByUid = useRef(new Map<string, File>())
  const thumbByUid = useRef(new Map<string, string>())

  const handleCropModalOk = (result: unknown) => {
    const file = result as (File & { uid?: string }) | null
    if (file instanceof File && file.uid) {
      croppedByUid.current.set(file.uid, file)
    }
  }

  const handleCropChange = async (fileList: UploadFile[]) => {
    const list = await Promise.all(
      fileList.map(async (item) => {
        let ready = readyByUid.current.get(item.uid)
        if (!ready) {
          const cropped = croppedByUid.current.get(item.uid)
          const source = cropped ?? (item.originFileObj as File | undefined)
          if (source && source.type?.startsWith('image/')) {
            ready = image ? await downscaleImageFile(source, image) : source
            readyByUid.current.set(item.uid, ready)
            // Preview the cropped result, not antd's pre-crop original.
            thumbByUid.current.set(item.uid, URL.createObjectURL(ready))
          }
        }
        if (!ready) return item
        return Object.assign({}, item, {
          status: 'done',
          size: ready.size,
          originFileObj: Object.assign(ready, { uid: item.uid }),
          thumbUrl: thumbByUid.current.get(item.uid),
        }) as UploadFile
      })
    )
    // Forget files no longer in the list so a later re-add re-crops cleanly.
    const alive = new Set(fileList.map((f) => f.uid))
    for (const uid of [...croppedByUid.current.keys()])
      if (!alive.has(uid)) croppedByUid.current.delete(uid)
    for (const uid of [...readyByUid.current.keys()])
      if (!alive.has(uid)) readyByUid.current.delete(uid)
    for (const uid of [...thumbByUid.current.keys()])
      if (!alive.has(uid)) {
        URL.revokeObjectURL(thumbByUid.current.get(uid)!)
        thumbByUid.current.delete(uid)
      }
    writeValue(list)
  }

  // Prevent any network request: the value is managed locally, not uploaded.
  const noopRequest: UploadProps['customRequest'] = ({ onSuccess }) => {
    setTimeout(() => onSuccess?.('ok'), 0)
  }

  const draggerContent = (
    <Space orientation="vertical">
      <Text style={{ fontSize: 30 }}>
        <InboxOutlined />
      </Text>
      {dragger.title && (
        <Text style={{ fontWeight: 'bold' }}>
          {dragger.title ?? 'FileClicOrDrag'}
        </Text>
      )}
      {dragger.subtitle && (
        <Text>
          <i>{dragger.subtitle}</i>
        </Text>
      )}
      <Space orientation="vertical" size={2}>
        {attachmentTypes().map((type) => (
          <Text key={type.id}>
            {type.name} - {type.extensions.join(', ')} - Max.
            {formatBytes(type.maxSize)}
          </Text>
        ))}
        {helpLine && (
          <Text type="secondary">
            <i>{helpLine}</i>
          </Text>
        )}
      </Space>
    </Space>
  )

  const accept = attachmentTypes()
    .map((at) => at.accept.join(','))
    .flat()
    .join(',')

  const draggerNode = (
    <Dragger
      maxCount={dragger.multiple ? undefined : 1}
      name={name as string}
      multiple={dragger.multiple}
      listType={'picture'}
      accept={accept}
      beforeUpload={() => false}
      onChange={({ fileList }) => {
        void handleChange(fileList)
      }}
    >
      {draggerContent}
    </Dragger>
  )

  const cropDraggerNode = (
    <Dragger
      // antd-img-crop can only crop one file per selection: it opens a single
      // modal and drops the rest when several files are picked at once. So the
      // input is single-select even when the uploader accumulates many images
      // (gallery) — the user adds them one at a time, each cropped in turn.
      maxCount={dragger.multiple ? undefined : 1}
      name={name as string}
      multiple={false}
      listType={'picture'}
      accept={accept}
      fileList={valueList}
      customRequest={noopRequest}
      onChange={({ fileList }) => {
        void handleCropChange(fileList)
      }}
    >
      {draggerContent}
    </Dragger>
  )

  return (
    <Space orientation="vertical" className="w-full">
      <Form.Item
        label={hideLabel ? undefined : label}
        name={name}
        rules={[...(formItem.rules ?? []), doesntExceedSize(attachmentTypes())]}
        valuePropName="fileList"
        getValueFromEvent={(e: any) => (Array.isArray(e) ? e : e?.fileList)}
        noStyle={noStyle}
      >
        {cropEnabled ? (
          <ImgCrop
            aspect={resolveAspect(image!) ?? 1}
            // Lossless here: the crop is an intermediate step, and
            // `downscaleImageFile` already re-encodes once at 0.9. Compressing
            // twice would land at ~0.81 on a JPEG for nothing.
            quality={1}
            zoomSlider
            rotationSlider
            showReset
            fillColor={CROP_FILL_COLOR}
            // Skip the crop modal for anything that is not an image (e.g. a PDF
            // in a mixed uploader): return false to bypass cropping entirely.
            beforeCrop={(file) =>
              file.type?.startsWith('image/') ? true : false
            }
            onModalOk={handleCropModalOk}
          >
            {cropDraggerNode}
          </ImgCrop>
        ) : (
          draggerNode
        )}
      </Form.Item>
    </Space>
  )
}

export default AttachmentEditor
